import { useEffect, useRef, useState } from "react";

const BB_BELOW_PINE_SCRIPT = `
//@version=5
strategy(shorttitle="Below BB Strategy", title="Below Bollinger Bands Strategy", overlay=true, commission_type=strategy.commission.percent, commission_value=0.1, slippage=0, default_qty_type=strategy.percent_of_equity, default_qty_value=100)

length = input.int(20, minval=1, title="Length")
maType = input.string("SMA", "Basis MA Type", options = ["SMA", "EMA", "SMMA (RMA)", "WMA", "VWMA"])
src = input(close, title="Source")
mult = input.float(2.0, minval=0.001, maxval=50, title="StdDev")
offset = input.int(0, "Offset", minval = -500, maxval = 500)
stopLoss = input.float(1.0, title="Stop Loss (%)") / 100
takeProfit = input.float(2.0, title="Take Profit (%)") / 100
minVolume = input.float(1000000, title="Minimum Volume")

startDate = input.time(title="Start Date", defval=timestamp("2018-01-01"), confirm=false)
endDate = input.time(title="End Date", defval=timestamp("2069-12-31"), confirm=false)
timeAllowed = time >= startDate and time <= endDate

ma(source, length, _type) =>
    switch _type
        "SMA" => ta.sma(source, length)
        "EMA" => ta.ema(source, length)
        "SMMA (RMA)" => ta.rma(source, length)
        "WMA" => ta.wma(source, length)
        "VWMA" => ta.vwma(source, length)

basis = ma(src, length, maType)
dev = mult * ta.stdev(src, length)
upper = basis + dev
lower = basis - dev

plot(basis, "Basis", color=#2962FF, offset=offset)
p1 = plot(upper, "Upper", color=#F23645, offset=offset)
p2 = plot(lower, "Lower", color=#089981, offset=offset)
fill(p1, p2, title="Background", color=color.rgb(33, 150, 243, 95))

enterLong = ta.crossunder(close, lower) and timeAllowed and volume >= minVolume
exitLong = ta.crossover(close, upper) and timeAllowed

if enterLong
    strategy.entry("Long", strategy.long)
    strategy.exit("Take Profit/Stop Loss", "Long", stop=close * (1 - stopLoss), limit=close * (1 + takeProfit))
if exitLong
    strategy.close("Long")
`;

interface ChartProps {
  height?: number;
  symbol?: keyof typeof SYMBOLS;
}

interface SymbolConfig {
  tvSymbol: string;
  isPremium: boolean;
  compareSymbol?: string;
}

const SYMBOLS: Record<string, SymbolConfig> = {
  "BTC/USDT": { tvSymbol: "BINANCE:BTCUSDT", isPremium: false },
  "ETH/USDT": { tvSymbol: "BINANCE:ETHUSDT", isPremium: false },
  "MSTR":     { tvSymbol: "NASDAQ:MSTR",      isPremium: false },
  "TSLA":     { tvSymbol: "NASDAQ:TSLA",      isPremium: false },
  "TLT":      { tvSymbol: "NASDAQ:TLT",       isPremium: false },
  "VIX":      { tvSymbol: "TVC:VIX",          isPremium: false },
  "ASST":     { tvSymbol: "NASDAQ:ASST",      isPremium: false },
  "SATA":     { tvSymbol: "NASDAQ:SATA",      isPremium: false },
  "STRC":     { tvSymbol: "NASDAQ:STRC",      isPremium: false },
  "SOL/USDT": { tvSymbol: "BINANCE:SOLUSDT",  isPremium: false },
  "SPCX":     { tvSymbol: "NASDAQ:SPCX",      isPremium: false },
  "TSLA/SPCX": { tvSymbol: "NASDAQ:TSLA",     isPremium: true, compareSymbol: "NASDAQ:SPCX" },
  "COINBASE PREMIUM": { tvSymbol: "COINBASE:BTCUSD", isPremium: true },
};

export default function Chart({ height = 400, symbol = "BTC/USDT" }: ChartProps) {
  const container = useRef<HTMLDivElement>(null);
  const [selectedSymbol, setSelectedSymbol] = useState<keyof typeof SYMBOLS>(symbol);

  useEffect(() => setSelectedSymbol(symbol), [symbol]);

  useEffect(() => {
    if (!SYMBOLS[selectedSymbol] || !container.current) return;

    container.current.innerHTML = "";

    const initWidget = () => {
      // @ts-ignore
      if (!window.TradingView) return;
      // @ts-ignore
      new window.TradingView.widget({
        autosize: true,
        symbol: SYMBOLS[selectedSymbol].tvSymbol,
        interval: "240",
        timezone: "Etc/UTC",
        theme: "dark",
        style: "1",
        locale: "en",
        container_id: "tradingview_74048",
        hide_top_toolbar: false,
        hide_legend: false,
        allow_symbol_change: true,
        save_image: false,
        toolbar: "default",
        enabled_features: ["show_hidden_items"],
        disabled_features: [],
        studies: ["MACD@tv-basicstudies", "BB@tv-basicstudies"],
        onChartReady: function(this: any) {
          this.activeChart().createStudy('Bollinger Bands', false, false);
          try {
            this.activeChart().createStudy('Pine Script', false, false, { script: BB_BELOW_PINE_SCRIPT });
          } catch {
            console.warn('Below BB strategy could not be loaded.');
          }
          if (SYMBOLS[selectedSymbol].isPremium) {
            this.activeChart().createStudy('Compare', false, false, {
              symbol: 'BINANCE:BTCUSDT',
              type: 'percent',
            });
          }
          // Handle pair trade (e.g., TSLA/SPCX)
          if ('compareSymbol' in SYMBOLS[selectedSymbol] && SYMBOLS[selectedSymbol].compareSymbol) {
            this.activeChart().createStudy('Compare', false, false, {
              symbol: SYMBOLS[selectedSymbol].compareSymbol,
              type: 'percent',
            });
          }
        },
      });
    };

    // @ts-ignore
    if (window.TradingView) {
      initWidget();
    } else {
      const existing = document.querySelector('script[src="https://s3.tradingview.com/tv.js"]');
      if (!existing) {
        const script = document.createElement("script");
        script.src = "https://s3.tradingview.com/tv.js";
        script.async = true;
        script.onload = initWidget;
        document.head.appendChild(script);
      } else {
        existing.addEventListener("load", initWidget);
      }
    }
  }, [selectedSymbol]);

  return (
    <div className="flex-1 flex flex-col items-center gap-6">
      <div
        id="tradingview_74048"
        ref={container}
        style={{ width: "100%", height }}
        className="bg-gray-800 rounded-xl shadow-xl border border-gray-700"
      />
    </div>
  );
}
